import {DEFAULT_FONT} from '@pinecast/common/constants';
import {deepChildrenMap} from '@pinecast/common/helpers/deepChildren';
import {useIsMobile} from '@pinecast/common/hooks/useIsMobile';
import DownChevronMedium from '@pinecast/common/icons/DownChevronMedium';
import type {IntlShape} from '@pinecast/i18n';
import {useIntl} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';
import type {PathMatch} from 'react-router-dom';
import {
  Link,
  matchPath,
  useNavigate,
  useLocation,
  useMatch,
} from 'react-router-dom';

import {usePageTitleEffect} from './usePageTitle';

type Props = {
  children: React.ReactNode;
  links: React.ReactNode;
};

type Route = {
  path: string;
  label: string;
  labelMessage: (intl: IntlShape) => string;
  matches: PathMatch | null;
};

const ChromeSidebar = ({children, links}: Props) => {
  const css = useCSS();
  const isMobile = useIsMobile();

  const intl = useIntl();

  const location = useLocation();

  const routes = deepChildrenMap(links, child => {
    if (!React.isValidElement(child)) {
      throw new Error('Unexpected child in chrome sidebar');
    }
    if (child.type !== ChromeSidebarItem) {
      throw new Error(
        `Child of type ${child.type} passed to ChromeSidebar as link`,
      );
    }
    const typed: React.ReactElement<
      ChromeSidebarItemProps,
      typeof ChromeSidebarItem
    > = child as any;
    return {
      path: typed.props.path,
      label: typed.props.labelMessage(intl),
      labelMessage: typed.props.labelMessage,
      matches: matchPath(
        {
          path: typed.props.path,
          end: true,
        },
        location.pathname,
      ),
    };
  });

  const currentPage = routes.find(x => x.matches)!;
  usePageTitleEffect(() => {
    if (!currentPage) {
      return;
    }
    // For now, we'll skip setting the page title from the sidebar
    // since we've changed the labelMessage type
    return;
  }, [currentPage?.label]);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
      })}
    >
      {isMobile ? (
        <ChromeSidebarMobileDropdown
          currentPage={currentPage}
          routes={routes}
        />
      ) : (
        <nav
          className={css({
            display: 'flex',
            flex: '0 0 160px',
            flexDirection: 'column',
            marginRight: '20px',
            paddingTop: '40px', // Page heading height plus margin
          })}
        >
          {links}
        </nav>
      )}
      <section className={css({flex: '1 1', maxWidth: '1024px'})}>
        {children}
      </section>
    </div>
  );
};

export default ChromeSidebar;

type ChromeSidebarItemProps = {
  beta?: boolean;
  children?: React.ReactNode;
  exact?: boolean;
  path: string;
  labelMessage: (intl: IntlShape) => string;
};
export const ChromeSidebarItem = ({
  beta,
  children,
  exact = true,
  labelMessage,
  path,
}: ChromeSidebarItemProps) => {
  const matches = !!useMatch({path: path, end: exact});
  const css = useCSS();
  const designColors = useColors();
  const intl = useIntl();
  // textLabel is extracted for use by the parent and is not consumed directly.
  return (
    <Link
      className={css({alignSelf: 'stretch', textDecoration: 'none'})}
      to={path}
    >
      <span
        className={css({
          alignSelf: 'flex-start',
          borderBottomRightRadius: '4px',
          borderTopRightRadius: '4px',
          borderBottomLeftRadius: '4px',
          borderTopLeftRadius: '4px',
          color: designColors.contentText,
          display: 'inline-block',
          fontWeight: 400,
          lineHeight: '16px',
          paddingBottom: '8px',
          paddingLeft: '12px',
          paddingTop: '8px',
          paddingRight: '12px',
          whiteSpace: 'nowrap',
          ...(matches && {
            backgroundColor: designColors.accentBlue,
            color: designColors.accentBlueText,
            fontWeight: 500,
          }),
        })}
      >
        {children || labelMessage(intl)}
      </span>
    </Link>
  );
};

const ChromeSidebarMobileDropdown = ({
  currentPage,
  routes,
}: {
  currentPage: Route;
  routes: Array<Route>;
}) => {
  const css = useCSS();
  const navigate = useNavigate();
  const designColors = useColors();

  return (
    <div
      className={css({
        backgroundColor: designColors.accentBlue,
        borderRadius: '4px',
        display: 'flex',
        fontSize: '14px',
        height: '32px',
        marginBottom: '16px',
        maxWidth: '100%',
        position: 'relative',
      })}
    >
      <DownChevronMedium
        color="#fff"
        style={{
          position: 'absolute',
          left: '13px',
          top: '2px',
          bottom: 0,
          margin: 'auto',
          pointerEvents: 'none',
        }}
      />
      <select
        className={css({
          appearance: 'none',
          MozAppearance: 'none',
          WebkitAppearance: 'none',
          backgroundColor: 'transparent',
          border: 0,
          color: '#fff',
          flexGrow: 1,
          fontFamily: DEFAULT_FONT,
          fontSize: '16px',
          fontWeight: 500,
          height: '32px',
          lineHeight: '32px',
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          marginLeft: 0,
          maxWidth: '100%',
          padding: '0 13px 0 33px',
          width: 'auto',
        })}
        value={currentPage?.path}
        onChange={e => {
          navigate(e.target.value);
        }}
      >
        {routes.map(route => (
          <option key={route.path} value={route.path}>
            {route.label}
          </option>
        ))}
      </select>
    </div>
  );
};
